import { z } from "zod";

import {
	AddToListMode,
	AssignStatus,
	InclusionFilterStatus,
	PriorityFilterStatus,
	SelectType,
} from "@/enum/list.enum";

import { selectAllScopeFields } from "./select-all-scope.schema";

/**
 * Request/response schemas for the List bulk actions — assign, re-run a prompt, re-score, and
 * copy/move to another list.
 *
 * Extracted from `schema/list.ts`, which is over the file-length ratchet and may only shrink;
 * `schema/list` re-exports this module, so existing import sites keep working. They belong
 * together anyway: every one of them is the "apply this to a selection" shape, and they all share
 * the same selection vocabulary (`selectType` + the filter-status arrays) that lets a caller act
 * on an explicit id list or on "everything matching the current filters".
 */

/** One lead in an assign request; also the element type of {@link BulkAssignSchema}. */
export const AssignSchema = z.object({
	listPersonId: z.number(),
	prospectId: z.number().optional(),
	listId: z.number().optional(),
	lemlistId: z.number().optional(),
	assignedToUserId: z.number().optional(),
	campaignId: z.string().optional(),
});

export const BulkAssignSchema = z.object({
	listId: z.number().optional(),
	assign: z.array(AssignSchema),
	assignStatus: z.nativeEnum(AssignStatus),
	selectType: z.nativeEnum(SelectType).optional(),
	inclusionFilterStatus: z.array(z.nativeEnum(InclusionFilterStatus)).optional(),
	filter: z.record(z.string(), z.any()).optional(),
	priorityFilterStatus: z.array(z.nativeEnum(PriorityFilterStatus)).optional(),
	// The table's search, tab statuses, reason sub-filters and signal filter (PREC-4441, PREC-4450).
	...selectAllScopeFields,
	senders: z.any().optional(),
	createReviewSession: z.boolean().optional(),
	/** PREC-4426: leave out the leads already connected to these LinkedIn senders. */
	skipConnectedToUserIds: z.array(z.number()).optional(),
});

export const BulkRunPromptRequestSchema = z.object({
	ids: z.array(z.number()).optional(),
	listId: z.number(),
	selectType: z.nativeEnum(SelectType).optional(),
	inclusionFilterStatus: z.array(z.nativeEnum(InclusionFilterStatus)).optional(),
	filter: z.record(z.string(), z.any()).optional(),
	priorityFilterStatus: z.array(z.nativeEnum(PriorityFilterStatus)).optional(),
	assignStatus: z.nativeEnum(AssignStatus).optional(),
	/** The table's search, tab statuses, reason sub-filters and signal filter (PREC-4443, PREC-4450). */
	...selectAllScopeFields,
});

export const BulkRunPromptResponseSchema = z.object({
	queued: z.number(),
	skipped: z.number(),
});

export const BulkRetriggerLeadScoringSchema = z.object({
	listPersonIds: z.array(z.number()),
	rescore: z.boolean(),
	selectType: z.nativeEnum(SelectType),
	inclusionFilterStatus: z.array(z.nativeEnum(InclusionFilterStatus)).optional(),
	priorityFilterStatus: z.array(z.nativeEnum(PriorityFilterStatus)).optional(),
	assignStatus: z.array(z.string()).optional(),
	filter: z.record(z.string(), z.any()).optional(),
	/** The table's search, tab statuses, reason sub-filters and signal filter (PREC-4443, PREC-4450). */
	...selectAllScopeFields,
});

export const BulkRetriggerLeadScoringResponseSchema = z.object({
	status: z.string(),
	scheduled: z.number(),
	skipped: z.number(),
	totalCreditsCharged: z.number(),
});

export const BulkAddToAnotherListSchema = z.object({
	listId: z.number(),
	listPersonIds: z.array(z.number()),
	selectType: z.nativeEnum(SelectType),
	assignStatus: z.array(z.string()).optional(),
	inclusionFilterStatus: z.array(z.nativeEnum(InclusionFilterStatus)).optional(),
	priorityFilterStatus: z.array(z.nativeEnum(PriorityFilterStatus)).optional(),
	filter: z.record(z.string(), z.any()).optional(),
	// The table's search, tab statuses, reason sub-filters and signal filter (PREC-4450).
	...selectAllScopeFields,
	targetListId: z.number().optional(),
	targetListName: z.string().optional(),
	mode: z.nativeEnum(AddToListMode).optional().default(AddToListMode.COPY),
});

export const BulkAddToAnotherListResponseSchema = z.object({
	status: z.string(),
	added: z.number(),
	skipped: z.number(),
	// Picked rows the transfer dropped (PREC-4595); absent from an answer of an older API.
	notTransferred: z.number().optional(),
	targetListId: z.number(),
});

export type BulkAssign = z.infer<typeof BulkAssignSchema>;
export type BulkRunPromptRequest = z.infer<typeof BulkRunPromptRequestSchema>;
export type BulkRunPromptResponse = z.infer<typeof BulkRunPromptResponseSchema>;
export type BulkRetriggerLeadScoring = z.infer<typeof BulkRetriggerLeadScoringSchema>;
export type BulkRetriggerLeadScoringResponse = z.infer<
	typeof BulkRetriggerLeadScoringResponseSchema
>;
export type BulkAddToAnotherList = z.infer<typeof BulkAddToAnotherListSchema>;
export type BulkAddToAnotherListResponse = z.infer<typeof BulkAddToAnotherListResponseSchema>;
